import { FC, JSX, use, useEffect, useState } from "react";
import "./AccessPagesList.scss";
import MasterPage from "../../../MasterPage/MasterPage";
import PageBase from "../../../../components/PageBase/PageBase";
import SkeletonTable from "../../../../components/SkeletonTable/SkeletonTable";
import AppPagination from "../../../../components/AppPagination/AppPagination";
import { toast } from "react-toastify";
import DropdownMenuTable from "../../../../components/DropdownMenuTable/DropdownMenuTable";
import { Dropdown } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit, faTrash } from "@fortawesome/free-solid-svg-icons";
import Swal from "sweetalert2";
import { usePaginate } from "../../../../providers/PaginateProvider";
import { useAccessPage } from "../../../../providers/Access/AccessPage/AccessPageProvider";
import { useSidebarFilter } from "../../../../providers/SidebarFilterProvider";
import moment from "moment";
import { AccessPageModel } from "../../../../models/Access/AccessPage/AccessPageModel";
import ModalAccessPageInsert from "../../../../components/Access/AccessPages/ModalAccessPageInsert/ModalAccessPageInsert";
import SidebarAccessPageFilter from "../../../../components/Access/AccessPages/SidebarAccessPageFilter/SidebarAccessPageFilter";
import { BvIcon } from "bevi-icon";

interface AccessPagesListProps { }

const AccessPagesList: FC<AccessPagesListProps> = () => {
  const [showModalInsert, setShowModalInsert] = useState<boolean>(false);

  const {
    pages,
    params,
    setParams,
    isLoading,
    error,
    accessPages,
    setAccessPage,
    handleList,
    handleDelete,
  } = useAccessPage();
  const { handlePaginate } = usePaginate();
  const { showSidebarFilter, setShowSidebarFilter } = useSidebarFilter();

  const handleEdit = (item: AccessPageModel) => {
    setAccessPage(item);
    setShowModalInsert(true);
  };

  const handleDeleteAccessPagesList = (item: AccessPageModel) => {
    Swal.fire({
      title: "<strong>Atenção?</strong>",
      html: `<span>Deseja excluir a página ${item?.name}?</span>`,
      icon: "question",
      confirmButtonText: "Sim, deletar!",
      cancelButtonText: "Cancelar",
      showCancelButton: true,
      confirmButtonColor: "var(--primary)",
    }).then(async (x: any) => {
      if (x.isConfirmed) {
        const ret = await handleDelete(item.id || 0).then();
        if (ret) {
          toast.success("Página inativada com sucesso!");
          handleList();
        } else {
          await Swal.fire(
            "Ops!",
            `Não foi possível inativar a página.<br>${error}`,
            "error"
          );
        }
      }
    });
  };

  const renderList = (): JSX.Element => {
    return (
      <>
        {accessPages?.length > 0 ? (
          <>
            {accessPages.map((x: AccessPageModel, key: number) => (
              <tr key={x.id} className="text-sm">
                <td className="text-center">{x.id}</td>
                <td>
                  <div className="p-1 d-flex align-items-center gap-2">
                    {x?.icon && (
                      <BvIcon
                        name={x?.icon ?? ""}
                        variant="solid"
                        weight={600}
                        width={20}
                        height={20}
                      />
                    )}
                    {x?.name}
                  </div>
                </td>
                <td>{x?.description}</td>
                <td className="text-center">
                  <span
                    className={
                      x?.deleted_at ? "badge bg-danger" : "badge bg-success"
                    }
                  >
                    {x?.deleted_at ? "Inativo" : "Ativo"}
                  </span>
                </td>
                <td className="text-center">
                  {moment(x?.created_at).format("DD/MM/YYYY HH:mm:ss")}
                </td>
                <td className="text-center">
                  <DropdownMenuTable key={key}>
                    <Dropdown.Item
                      className="border-radius-md py-2"
                      onClick={() => handleEdit(x)}
                    >
                      <FontAwesomeIcon icon={faEdit} className="me-2" /> Editar
                    </Dropdown.Item>
                    {!x?.deleted_at && (
                      <Dropdown.Item
                        className="border-radius-md py-2"
                        onClick={() => handleDeleteAccessPagesList(x)}
                      >
                        <FontAwesomeIcon icon={faTrash} className="me-2" />{" "}
                        Remover
                      </Dropdown.Item>
                    )}
                  </DropdownMenuTable>
                </td>
              </tr>
            ))}
          </>
        ) : (
          <tr>
            <td colSpan={6} className="text-center text-sm">
              Não há registros
            </td>
          </tr>
        )}
      </>
    );
  };

  const renderContent = (): JSX.Element => {
    return (
      <>
        {!isLoading ? (
          <div>
            <div className="table-responsive">
              <table className="table table-flush m-0 w-100">
                <thead className="thead-light">
                  <tr>
                    <th
                      className="text-uppercase opacity-7 text-center"
                      scope="col"
                    >
                      #
                    </th>
                    <th className="text-uppercase opacity-7" scope="col">
                      Nome
                    </th>
                    <th className="text-uppercase opacity-7" scope="col">
                      Descrição
                    </th>
                    <th
                      className="text-uppercase opacity-7 text-center"
                      scope="col"
                    >
                      Status
                    </th>
                    <th
                      className="text-uppercase opacity-7 text-center"
                      scope="col"
                    >
                      Data de Cadastro
                    </th>
                    <th
                      className="text-uppercase opacity-7 text-center"
                      scope="col"
                    >
                      Ação
                    </th>
                  </tr>
                </thead>
                <tbody>{renderList()}</tbody>
              </table>
            </div>

            {accessPages?.length > 0 && (
              <div>
                <nav aria-label="...">
                  <AppPagination
                    pages={pages}
                    handlePaginate={handlePaginate}
                    params={params}
                    setParams={setParams}
                    style={{ backgroundColor: "#fff" }}
                  />
                </nav>
              </div>
            )}
          </div>
        ) : (
          <SkeletonTable />
        )}
      </>
    );
  };

  useEffect(() => {
    setParams({ ...params, page: 1 });
  }, []);

  return (
    <MasterPage title="Páginas de Acesso" icon="fas fa-file" footerFixed>
      <div className="AccessPagesList" data-testid="AccessPagesList">
        <PageBase
          title="Páginas de Acesso"
          subtitle="Gerenciamento de Páginas de Acesso do sistema."
          handleInsert={() => setShowModalInsert(true)}
          handleFilter={() =>
            !!showSidebarFilter
              ? setShowSidebarFilter(false)
              : setShowSidebarFilter(true)
          }
          error={error}
          children={renderContent()}
          hasFilter
          useButtonGroup
        />

        <ModalAccessPageInsert
          show={showModalInsert}
          onClose={() => setShowModalInsert(false)}
        />
      </div>

      <SidebarAccessPageFilter />
    </MasterPage>
  );
};

export default AccessPagesList;
